import { useTranslation } from "react-i18next";
import { twMerge } from "tailwind-merge";
import {
  Building05,
  MarkerPin01,
  Clock,
  ChevronRight,
} from "untitledui-js/react";
import { Link, useParams } from "react-router-dom";
import { Typography, Button } from "@pondigitalsolutions/pon-component-library";

import type { VacancyCardProps, CompanyLogoProps } from "../types/VacancyTypes";
import { stripHtmlAndTruncate } from "../utils/stripHtmlAndTruncate";
import { DetailItem } from "./VacancyDetailItem";
import { useCurrentLang } from "@hooks/useCurrentLang";
import { languageDutch } from "../i18n";

const CompanyLogo = ({ logoSrc, size, alt }: CompanyLogoProps) => {
  const sizeClasses = size === "lg" ? "h-10 w-10" : "h-8 w-8";

  if (!logoSrc)
    return (
      <div
        className={twMerge("mr-1.5", sizeClasses)}
        data-testid="company-logo-placeholder"
        aria-hidden
      />
    );

  return (
    <div
      className={twMerge(
        "mr-1.5 flex items-center justify-center rounded-lg bg-tertiary-50 p-0.5 dark:bg-tertiary-200",
        sizeClasses
      )}
      data-testid="company-logo"
    >
      <img
        className="max-h-full max-w-full rounded-md"
        src={logoSrc}
        alt={alt}
      />
    </div>
  );
};

export const VacancyCard = ({
  vacancy,
  tabIndex,
  "data-vacancy-card": dataVacancyCard,
}: VacancyCardProps) => {
  const { t } = useTranslation();
  const { lang } = useParams();
  const currentLang = useCurrentLang(lang);

  const hoursDisplay =
    vacancy.minHours && vacancy.maxHours
      ? `${vacancy.minHours} - ${vacancy.maxHours} ${t("Vacancies.hoursPerWeek")}`
      : "";

  // Use the data-vacancy-card prop to determine if this is a compact footer layout
  const isCompactLayout = dataVacancyCard === true;

  // Strip HTML and truncate description
  const cleanDescription = vacancy.description
    ? stripHtmlAndTruncate(vacancy.description)
    : "";

  // Company logo alt text
  const companyLogoAlt = `${vacancy.company.name} ${t("Global.logo")}`;

  return (
    <>
      {isCompactLayout ? (
        <Link
          to={
            currentLang === languageDutch
              ? `/vacancies/${vacancy.slug}`
              : `/${currentLang}/vacancies/${vacancy.slug}`
          }
          className="block rounded-lg border-0 bg-white transition-shadow duration-200 hover:shadow-sm dark:bg-white/10"
          tabIndex={tabIndex}
          data-vacancy-card={dataVacancyCard}
          data-testid="vacancy-card-compact"
        >
          <div
            className={`flex flex-row items-center justify-start gap-1 p-2.5 max-md:text-nowrap`}
          >
            <CompanyLogo
              logoSrc={vacancy.company.logo}
              size="sm"
              alt={companyLogoAlt}
            />
            <div className="flex flex-col">
              <Typography
                as="span"
                className="text-sm font-normal text-secondary-800 dark:text-white"
              >
                {vacancy.title}
              </Typography>
              {vacancy.location && (
                <Typography
                  as="span"
                  className="text-xs font-normal text-tertiary-800 dark:text-tertiary-200"
                >
                  {vacancy.location}
                </Typography>
              )}
            </div>
          </div>
        </Link>
      ) : (
        // Expanded layout for the vacancies page
        <div
          className="flex h-full flex-col bg-tertiary-100 p-8 dark:bg-secondary-900"
          data-testid="vacancy-card-expanded"
        >
          {vacancy.department && (
            <Typography
              as="span"
              className="trim-both-cap-alphabetic self-start rounded-lg border border-tertiary-50 bg-tertiary-50 px-3 py-2 text-xs text-pretty text-secondary-700 dark:border-white/20 dark:bg-white/10 dark:text-white"
            >
              {vacancy.department}
            </Typography>
          )}
          <Typography
            as="h3"
            className="line-height-[125%] mt-4 mb-6 text-xl text-pretty break-words text-secondary-700 md:mb-8 md:text-2xl dark:text-white"
          >
            {vacancy.title}
          </Typography>
          <div className="flex h-full flex-col gap-2">
            <DetailItem icon={Building05}>{vacancy.company.name}</DetailItem>
            {vacancy.location && (
              <DetailItem icon={MarkerPin01}>{vacancy.location}</DetailItem>
            )}
            {hoursDisplay && (
              <DetailItem icon={Clock}>{hoursDisplay}</DetailItem>
            )}
            {cleanDescription && (
              <Typography className="mt-4 text-sm leading-[150%] font-normal text-tertiary-900 md:mt-6 dark:text-tertiary-100">
                {cleanDescription}
              </Typography>
            )}
          </div>
          <div className="mt-6 flex items-center justify-between md:mt-8">
            <Button
              id="vacancy-view-details-button"
              variant="primary"
              size="xl"
              className="w-fit gap-2 md:flex"
              renderProp={
                <Link
                  to={
                    currentLang === languageDutch
                      ? `/vacancies/${vacancy.slug}`
                      : `/${currentLang}/vacancies/${vacancy.slug}`
                  }
                />
              }
            >
              {t("Vacancies.viewVacancy")}
              <ChevronRight className="h-5 w-5" />
            </Button>
            <CompanyLogo
              logoSrc={vacancy.company.logo}
              size="lg"
              alt={companyLogoAlt}
            />
          </div>
        </div>
      )}
    </>
  );
};
